import React, { useEffect, useRef, useState } from 'react';
import {
    Heart, ArrowRight, Stethoscope, Users, Bed, ShoppingCart, Calendar, Syringe, FileText, Activity,
    Smartphone, MapPin, UserPlus, CreditCard, ClipboardList, PawPrint, ShieldCheck,
} from 'lucide-react';

/**
 * Portada pública de /veterinarias (pestaña "Descubrir" de VetPortalView).
 *
 * Regla de contenido: SOLO lo que existe hoy en el código (verificado el 2026-10-12):
 *  - Mis pacientes: buscar la mascota de un dueño en Amigo Peludo y vincularla, o registrarla (searchPets/linkVetPatient/addPet).
 *  - Ficha clínica: historial médico, carnet sanitario, consulta con signos vitales (peso, temperatura, FC, TLLC),
 *    plantillas de consulta, recetas, archivos y documentos (VetConsultationWorkspace, DocumentManager).
 *  - Lo que la clínica registra (health.php / vaccines.php por mascota) es lo mismo que el dueño ve en su app (PetEnvironment).
 *  - Hospitalizados: ingreso con jaula y gravedad, tratamientos y tareas periódicas con aviso de atraso, bitácora,
 *    consumo de insumos que descuenta stock, alta y marca de cobrado (HospitalizationPanel).
 *  - Punto de venta: caja (apertura/cierre), productos y servicios con stock, reportes, cobro con terminal
 *    Tuu o Mercado Pago (API) o Transbank (confirmación manual) (VetPOS, vet_payment_terminal.php).
 *  - Agenda: vista día / semana / mes y nuevas citas creadas por la clínica.
 *  - Equipo: acceso para el personal de la clínica (vet_staff.php).
 *  - Mapa: al aprobarse la solicitud de socio, la clínica queda en la tabla businesses que muestra el mapa.
 * NO se promete: reserva de citas por los dueños (hoy no llega a la agenda de la clínica), recordatorios
 * automáticos, comunicaciones masivas, boleta electrónica SII ni planes de pago.
 *
 * Las capturas de /public/veterinarias son del panel real con datos de ejemplo.
 */

const unsplash = (id: string, w = 900, h?: number) =>
    `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&q=80&w=${w}${h ? `&h=${h}` : ''}`;

const SHOT = {
    panelDesktop: '/portadas/veterinarias/panel-escritorio.webp',
    panelMobile: '/portadas/veterinarias/panel-movil.webp',
    pacientes: '/portadas/veterinarias/etapa-1-pacientes.webp',
    ficha: '/portadas/veterinarias/etapa-2-ficha.webp',
    hosp: '/portadas/veterinarias/etapa-3-hospitalizados.webp',
    pos: '/portadas/veterinarias/etapa-4-punto-de-venta.webp',
    agenda: '/portadas/veterinarias/agenda.webp',
    agendaVideo: '/portadas/veterinarias/vet-agenda.webm',
    agendaPoster: '/portadas/veterinarias/vet-agenda-poster.webp',
};

type Step = { icon: React.ElementType; title: string; text: string; shot: string; alt: string; caption: string };
const JOURNEY: Step[] = [
    {
        icon: Users, title: 'Llega a tu consulta', shot: SHOT.pacientes, alt: 'Listado de mascotas vinculadas a la clínica', caption: 'Panel · Mis pacientes',
        text: 'Si el dueño ya usa Amigo Peludo, buscas a su mascota y la vinculas a tu clínica. Si no, la registras en segundos.',
    },
    {
        icon: Stethoscope, title: 'La atiendes', shot: SHOT.ficha, alt: 'Ficha clínica de un paciente con historial y carnet', caption: 'Panel · Ficha clínica',
        text: 'Abres su ficha: historial médico, carnet sanitario, consulta con signos vitales, plantillas, recetas y archivos, todo en un mismo lugar.',
    },
    {
        icon: Bed, title: 'Si necesita quedarse', shot: SHOT.hosp, alt: 'Pacientes hospitalizados con jaula y gravedad', caption: 'Panel · Hospitalizados',
        text: 'Lo ingresas con su jaula y gravedad. Programas tratamientos y tareas periódicas, y el panel te avisa si alguna se atrasa.',
    },
    {
        icon: ShoppingCart, title: 'Y se va a casa', shot: SHOT.pos, alt: 'Punto de venta de la clínica', caption: 'Panel · Punto de venta',
        text: 'Le das el alta y cobras desde la caja: servicios, vacunas y productos con su stock, y pago con terminal Tuu, Mercado Pago o Transbank.',
    },
];

const VALUES = ['Consultas', 'Vacunas', 'Hospitalización', 'Recetas', 'Historial clínico', 'Agenda', 'Caja y stock', 'Cuidado con cariño'];

/* ─────────────────────────── estilos y animaciones ─────────────────────────── */
const HomeStyles: React.FC = () => (
    <>
        <link rel="stylesheet" precedence="default"
            href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,650;1,9..144,500;1,9..144,600&display=swap" />
        <style>{`
            .vh-serif { font-family: 'Fraunces', Georgia, serif; }
            .vh-grad-text { background: linear-gradient(100deg, #0ea5a4, #0284c7 60%, #6366f1); -webkit-background-clip: text; background-clip: text; color: transparent; }
            @keyframes vhFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-12px) rotate(var(--r,0deg)); } }
            @keyframes vhBlob { 0%,100% { border-radius: 42% 58% 63% 37% / 41% 44% 56% 59%; transform: translate(0,0) scale(1); } 50% { border-radius: 58% 42% 38% 62% / 55% 61% 39% 45%; transform: translate(3%, -4%) scale(1.08); } }
            @keyframes vhBeat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.18); } 28% { transform: scale(1); } 42% { transform: scale(1.12); } 70% { transform: scale(1); } }
            @keyframes vhMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
            @keyframes vhPop { 0% { opacity: 0; transform: translateY(14px) scale(.96); } 100% { opacity: 1; transform: none; } }
            @keyframes vhShine { 0% { transform: translateX(-120%) skewX(-18deg); } 60%,100% { transform: translateX(260%) skewX(-18deg); } }
            /* Línea de latido (ECG) que se dibuja una y otra vez */
            @keyframes vhEcg { 0% { stroke-dashoffset: 1000; } 70% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: 0; opacity: 0; } }
            @keyframes vhTravel { 0% { left: 0; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { left: calc(100% - 12px); opacity: 0; } }
            .vh-ecg { stroke-dasharray: 1000; animation: vhEcg 3.2s ease-in-out infinite; }
            .vh-float { animation: vhFloat 6s ease-in-out infinite; }
            .vh-blob { animation: vhBlob 14s ease-in-out infinite; }
            .vh-beat { animation: vhBeat 1.4s ease-in-out infinite; }
            .vh-marquee { animation: vhMarquee 34s linear infinite; }
            .vh-pop { animation: vhPop .8s cubic-bezier(.2,.8,.2,1) both; }
            .vh-reveal { opacity: 0; transform: translateY(28px); transition: opacity .9s cubic-bezier(.2,.8,.2,1), transform .9s cubic-bezier(.2,.8,.2,1); }
            .vh-reveal.is-in { opacity: 1; transform: none; }
            .vh-line { transform-origin: top; transform: scaleY(0); transition: transform 1.8s cubic-bezier(.4,0,.2,1); }
            .is-in .vh-line { transform: scaleY(1); }
            .vh-dot { animation: vhTravel 2.6s ease-in-out infinite; }
            @media (prefers-reduced-motion: reduce) {
                .vh-float, .vh-blob, .vh-beat, .vh-marquee, .vh-pop, .vh-ecg, .vh-dot { animation: none !important; }
                .vh-ecg { stroke-dasharray: none; }
                .vh-reveal, .vh-line { opacity: 1; transform: none; transition: none; }
            }
        `}</style>
    </>
);

const useInView = <T extends HTMLElement>(threshold = 0.2) => {
    const ref = useRef<T>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || inView) return;
        if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { threshold });
        io.observe(el);
        return () => io.disconnect();
    }, [inView, threshold]);
    return [ref, inView] as const;
};

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({ children, delay = 0, className = '' }) => {
    const [ref, inView] = useInView<HTMLDivElement>(0.15);
    return <div ref={ref} className={`vh-reveal ${inView ? 'is-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = 'text-teal-600' }) => (
    <p className={`text-[11px] font-bold uppercase tracking-[0.28em] mb-3 ${className}`}>{children}</p>
);

const BrowserFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-2xl overflow-hidden bg-white ring-1 ring-teal-100 shadow-[0_30px_60px_-28px_rgba(15,60,70,.45)] ${className}`}>
        <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#f1f8f8] border-b border-teal-100">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff8a7a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#ffd27a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9be0a6]" />
            <span className="ml-3 flex-1 truncate rounded-full bg-white px-3 py-0.5 text-[10px] text-[#6b8a90] ring-1 ring-teal-100">amigopeludo.cl/veterinarias</span>
        </div>
        <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
    </div>
);

// Mini video en marco de navegador: se reproduce solo, sin sonido y en bucle, solo mientras está en pantalla.
// Con "reducir movimiento" activo queda la imagen fija (poster).
const VideoFrame: React.FC<{ src: string; poster: string; label: string; className?: string }> = ({ src, poster, label, className = '' }) => {
    const ref = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        const v = ref.current;
        if (!v || typeof IntersectionObserver === 'undefined') return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) v.play().catch(() => {}); else v.pause(); }, { threshold: 0.35 });
        io.observe(v);
        return () => io.disconnect();
    }, []);
    return (
        <div className={`rounded-2xl overflow-hidden bg-white ring-1 ring-teal-100 shadow-[0_30px_60px_-28px_rgba(15,60,70,.45)] ${className}`}>
            <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-[#f1f8f8] border-b border-teal-100">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff8a7a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#ffd27a]" /><span className="w-2.5 h-2.5 rounded-full bg-[#9be0a6]" />
                <span className="ml-3 flex-1 truncate rounded-full bg-white px-3 py-0.5 text-[10px] text-[#6b8a90] ring-1 ring-teal-100">amigopeludo.cl/veterinarias</span>
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-500 ring-1 ring-rose-100"><span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />{label}</span>
            </div>
            <video ref={ref} src={src} poster={poster} muted loop playsInline preload="metadata" aria-label={label} className="block w-full h-auto aspect-[1280/760] bg-[#f6fafb]" />
        </div>
    );
};

const PhoneFrame: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => (
    <div className={`rounded-[2.4rem] bg-[#0f2f3a] p-2 shadow-[0_30px_60px_-24px_rgba(10,40,50,.6)] ${className}`}>
        <div className="relative rounded-[2rem] overflow-hidden bg-white">
            <span className="absolute top-2 left-1/2 -translate-x-1/2 z-10 w-16 h-4 rounded-full bg-[#0f2f3a]" />
            <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
        </div>
    </div>
);

/** Latido animado: el detalle "clínico" de la portada. */
const Heartbeat: React.FC<{ className?: string; color?: string }> = ({ className = '', color = '#0ea5a4' }) => (
    <svg aria-hidden viewBox="0 0 600 80" preserveAspectRatio="none" className={className} fill="none">
        <path className="vh-ecg" d="M0 40 H170 L190 40 L205 12 L222 70 L238 22 L250 40 H330 L345 40 L355 30 L365 40 H600"
            stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────── secciones ─────────────────────────── */
const Hero: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#f2fbfa] via-[#eaf6fb] to-[#eef1ff]">
        <div aria-hidden className="vh-blob absolute -top-32 -left-24 w-[34rem] h-[34rem] bg-gradient-to-br from-teal-200/60 to-emerald-100/40 blur-3xl" />
        <div aria-hidden className="vh-blob absolute -bottom-40 right-[-10rem] w-[38rem] h-[38rem] bg-gradient-to-tr from-sky-200/60 to-indigo-200/40 blur-3xl" style={{ animationDelay: '-6s' }} />
        <Heartbeat className="absolute left-0 right-0 bottom-24 w-full h-16 opacity-25" />

        <div className="relative max-w-7xl ml-auto mr-auto px-6 pt-28 md:pt-36 pb-20 md:pb-28 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <div className="text-center lg:text-left">
                <span className="vh-pop inline-flex items-center gap-2 bg-white/75 backdrop-blur border border-teal-100 text-teal-700 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] shadow-sm">
                    <Heart size={13} className="vh-beat fill-rose-400 text-rose-400" /> Para clínicas y médicos veterinarios
                </span>
                <h1 className="vh-pop vh-serif mt-6 text-[2.6rem] sm:text-6xl lg:text-[4.2rem] leading-[1.03] font-semibold text-[#0f2f3a] tracking-[-0.02em]" style={{ animationDelay: '.1s' }}>
                    Medicina con <em className="vh-grad-text italic">corazón</em>, clínica sin papeles
                </h1>
                <p className="vh-pop mt-6 text-lg text-[#4f6f76] leading-relaxed max-w-xl ml-auto mr-auto lg:ml-0 lg:mr-0" style={{ animationDelay: '.2s' }}>
                    Pacientes, fichas clínicas, hospitalización, agenda y caja en un solo panel, en el computador o en el celular. Y lo que registras de cada mascota llega a la app de su familia.
                </p>
                <div className="vh-pop mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start" style={{ animationDelay: '.3s' }}>
                    <button onClick={onRegister}
                        className="group relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-[#0ea5a4] to-[#0284c7] text-white font-bold text-sm tracking-wide shadow-[0_18px_40px_-14px_rgba(2,132,199,.65)] hover:-translate-y-0.5 transition-all">
                        <span aria-hidden className="absolute inset-y-0 left-0 w-1/3 bg-white/30" style={{ animation: 'vhShine 3.2s ease-in-out infinite' }} />
                        Solicitar cuenta para mi clínica <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button onClick={onLogin}
                        className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/80 backdrop-blur border border-teal-100 text-[#24505a] font-bold text-sm tracking-wide hover:bg-white transition-all">
                        Ya tengo cuenta
                    </button>
                </div>
            </div>

            <div className="relative ml-auto mr-auto w-full max-w-[38rem] pb-10 sm:pb-16">
                <div className="vh-pop relative" style={{ animationDelay: '.15s' }}>
                    <BrowserFrame src={SHOT.panelDesktop} alt="Panel de control de la clínica en el computador" />
                </div>
                <div className="vh-float absolute -right-2 sm:-right-6 -bottom-2 w-[34%] max-w-[11.5rem]" style={{ ['--r' as any]: '3deg' }}>
                    <PhoneFrame src={SHOT.panelMobile} alt="Panel de la clínica en el celular" />
                </div>
                <div className="vh-float absolute -left-3 sm:-left-8 -bottom-4 w-28 sm:w-36 aspect-square rounded-[1.75rem] overflow-hidden ring-[6px] ring-white shadow-xl" style={{ animationDelay: '-3s', ['--r' as any]: '-4deg' }}>
                    <img src={unsplash('1543466835-00a7907e9de1', 300, 300)} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="absolute -bottom-6 left-0 right-0 text-center text-[10px] text-[#8aa5aa] italic">Capturas reales del panel, con datos de ejemplo</p>
            </div>
        </div>
        <svg aria-hidden viewBox="0 0 1440 90" preserveAspectRatio="none" className="relative block w-full h-12 md:h-20 text-white" fill="currentColor">
            <path d="M0 90V45C160 10 330 0 520 18c190 18 300 62 500 58 170-3 290-40 420-58v72H0Z" />
        </svg>
    </section>
);

const ValuesMarquee: React.FC = () => (
    <div className="bg-white py-6 overflow-hidden border-b border-teal-50">
        <div className="vh-marquee flex w-max gap-10 whitespace-nowrap">
            {[...VALUES, ...VALUES].map((v, i) => (
                <span key={i} className="vh-serif italic text-2xl md:text-3xl text-[#a9cfd2] flex items-center gap-10">
                    {v} <PawPrint size={18} className="text-teal-300 not-italic" />
                </span>
            ))}
        </div>
    </div>
);

const Journey: React.FC = () => {
    const [ref, inView] = useInView<HTMLDivElement>(0.08);
    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-2xl ml-auto mr-auto mb-16">
                    <Eyebrow>El recorrido de un paciente</Eyebrow>
                    <h2 className="vh-serif text-4xl md:text-5xl font-semibold text-[#0f2f3a] tracking-[-0.02em] leading-tight">De la consulta <em className="vh-grad-text italic">de vuelta a casa</em></h2>
                    <p className="mt-4 text-[#4f6f76] leading-relaxed">Cada etapa con la pantalla que usa tu equipo. Todo queda en la ficha del paciente.</p>
                </Reveal>

                <div ref={ref} className={`relative ${inView ? 'is-in' : ''}`}>
                    <div aria-hidden className="vh-line absolute left-6 md:left-1/2 top-2 bottom-2 w-[3px] md:-translate-x-1/2 rounded-full bg-gradient-to-b from-teal-300 via-sky-400 to-indigo-400" />
                    <div className="space-y-14 md:space-y-24">
                        {JOURNEY.map((s, i) => (
                            <Reveal key={s.title} delay={i * 100} className="relative grid md:grid-cols-2 gap-6 md:gap-16 items-center pl-16 md:pl-0">
                                <span className="absolute left-6 md:left-1/2 top-6 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 w-12 h-12 rounded-full bg-white border-4 border-teal-100 shadow-lg flex items-center justify-center z-10">
                                    <s.icon size={20} className="text-teal-600" />
                                </span>
                                <div className={`${i % 2 ? 'md:order-2 md:pl-12' : 'md:text-right md:pr-12'}`}>
                                    <p className="vh-serif text-teal-500 text-lg italic">Paso {i + 1}</p>
                                    <h3 className="vh-serif text-2xl md:text-3xl font-semibold text-[#0f2f3a] mt-1">{s.title}</h3>
                                    <p className="text-[#4f6f76] leading-relaxed mt-3 md:max-w-sm md:inline-block">{s.text}</p>
                                </div>
                                <figure className={`${i % 2 ? 'md:order-1 md:pr-12' : 'md:pl-12'}`}>
                                    <BrowserFrame src={s.shot} alt={s.alt} />
                                    <figcaption className="mt-3 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8aa5aa]">{s.caption}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

/** La clínica y la familia ven la misma información: diagrama animado de la sincronización. */
const SharedRecord: React.FC = () => (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-[#f4fbfb] to-[#eef4ff]">
        <div aria-hidden className="vh-blob absolute top-10 -right-32 w-[26rem] h-[26rem] bg-rose-100/60 blur-3xl" />
        <div className="relative max-w-6xl ml-auto mr-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
            <Reveal>
                <Eyebrow className="text-rose-500">Con la familia</Eyebrow>
                <h2 className="vh-serif text-4xl md:text-5xl font-semibold text-[#0f2f3a] tracking-[-0.02em] leading-tight">Lo que registras, <em className="italic text-rose-500">ellos lo ven</em></h2>
                <p className="mt-5 text-[#4f6f76] leading-relaxed">Cuando el paciente está vinculado a la cuenta de su familia en Amigo Peludo, las consultas y vacunas que guardas en su ficha aparecen en el perfil de la mascota en su app. Sin carnet de papel que se pierda.</p>
                <ul className="mt-7 space-y-3 text-sm text-[#24505a]">
                    {([
                        [Syringe, 'Vacunas y desparasitaciones en su carnet digital'],
                        [ClipboardList, 'Consultas con diagnóstico, detalle y recomendaciones'],
                        [ShieldCheck, 'Un solo historial, aunque la mascota cambie de clínica'],
                    ] as [React.ElementType, string][]).map(([Icon, t]) => (
                        <li key={t} className="flex items-start gap-3"><span className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0"><Icon size={14} className="text-teal-600" /></span><span className="pt-1">{t}</span></li>
                    ))}
                </ul>
            </Reveal>

            <Reveal delay={150}>
                <div className="relative flex items-center justify-between gap-4 max-w-lg ml-auto mr-auto">
                    <div className="w-[44%] rounded-[1.75rem] bg-white p-5 shadow-[0_30px_60px_-30px_rgba(15,60,70,.5)] ring-1 ring-teal-100">
                        <span className="w-11 h-11 rounded-2xl bg-teal-50 flex items-center justify-center"><Stethoscope size={20} className="text-teal-600" /></span>
                        <p className="vh-serif text-lg font-semibold text-[#0f2f3a] mt-3 leading-tight">Tu clínica</p>
                        <p className="text-xs text-[#6b8a90] mt-1">Guardas una vacuna en la ficha</p>
                        <div className="mt-3 rounded-xl bg-teal-50 px-3 py-2 text-[11px] font-semibold text-teal-700 flex items-center gap-1.5"><Syringe size={12} /> Antirrábica</div>
                    </div>
                    <div className="relative flex-1 h-10">
                        <span className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-teal-200" />
                        <span className="vh-dot absolute left-0 top-1/2 w-3 h-3 -mt-1.5 rounded-full bg-rose-400 shadow-[0_0_12px_rgba(251,113,133,.9)]" />
                        <Heart size={18} className="vh-beat absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-rose-400 fill-rose-400" />
                    </div>
                    <div className="w-[44%] rounded-[1.75rem] bg-white p-5 shadow-[0_30px_60px_-30px_rgba(15,60,70,.5)] ring-1 ring-rose-100">
                        <span className="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center"><Smartphone size={20} className="text-rose-500" /></span>
                        <p className="vh-serif text-lg font-semibold text-[#0f2f3a] mt-3 leading-tight">Su familia</p>
                        <p className="text-xs text-[#6b8a90] mt-1">La ve en la app de Amigo Peludo</p>
                        <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-600 flex items-center gap-1.5"><ShieldCheck size={12} /> Carnet al día</div>
                    </div>
                </div>
                <p className="text-center text-[10px] text-[#8aa5aa] italic mt-6">Diagrama ilustrativo</p>
            </Reveal>
        </div>
    </section>
);

/** Más herramientas del panel, todas existentes. */
const Toolkit: React.FC = () => {
    const items: [React.ElementType, string, string][] = [
        [Calendar, 'Agenda médica', 'Vista por día, semana o mes, con bloques horarios y nuevas citas.'],
        [Activity, 'Consulta completa', 'Signos vitales (peso, temperatura, FC, TLLC) y plantillas para no partir de cero.'],
        [FileText, 'Recetas y archivos', 'Recetas, archivos y documentos guardados en la ficha de cada paciente.'],
        [Bed, 'Hospitalización', 'Tratamientos, bitácora y consumo de insumos que descuenta stock solo.'],
        [CreditCard, 'Caja y cobros', 'Apertura y cierre de caja, reportes y cobro con terminal Tuu, Mercado Pago o Transbank.'],
        [UserPlus, 'Tu equipo', 'Registra a tu personal (nombre, correo y RUT) en la configuración de la clínica.'],
        [MapPin, 'En el mapa', 'Con tu clínica aprobada, aparece en el mapa de Amigo Peludo.'],
        [Smartphone, 'Donde estés', 'El mismo panel en el computador o en el celular.'],
    ];
    return (
        <section className="bg-white py-20 md:py-28">
            <div className="max-w-6xl ml-auto mr-auto px-6">
                <Reveal className="text-center max-w-3xl ml-auto mr-auto mb-10">
                    <Eyebrow>Todo el día de la clínica</Eyebrow>
                    <h2 className="vh-serif text-4xl md:text-6xl font-semibold text-[#0f2f3a] tracking-[-0.02em] leading-tight">Agendar nunca fue <em className="vh-grad-text italic">tan fácil</em></h2>
                    <p className="mt-5 text-lg text-[#4f6f76] leading-relaxed">Así llega una cita pedida desde la app: aparece <strong className="text-[#0f2f3a]">por confirmar</strong> en tu agenda, la abres, la confirmas y el dueño recibe el aviso por correo.</p>
                </Reveal>
                <Reveal className="mb-14">
                    <VideoFrame src={SHOT.agendaVideo} poster={SHOT.agendaPoster} label="Confirmando una cita" className="w-full" />
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {items.map(([Icon, title, text], i) => (
                        <Reveal key={title} delay={(i % 4) * 80}>
                            <div className="h-full rounded-[1.5rem] bg-gradient-to-br from-[#f6fbfb] to-[#f3f6ff] ring-1 ring-teal-100/70 p-5 hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(15,60,70,.45)] transition-all">
                                <span className="w-11 h-11 rounded-2xl bg-white shadow-sm flex items-center justify-center"><Icon size={20} className="text-teal-600" /></span>
                                <p className="font-bold text-[#0f2f3a] mt-4">{title}</p>
                                <p className="text-sm text-[#4f6f76] leading-relaxed mt-1.5">{text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

const FinalCta: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-[#0f2f3a] via-[#124553] to-[#1e3a6e] text-white">
        <div aria-hidden className="vh-blob absolute -top-24 right-[-6rem] w-[30rem] h-[30rem] bg-teal-400/20 blur-3xl" />
        <Heartbeat className="absolute left-0 right-0 bottom-10 w-full h-16 opacity-25" color="#5eead4" />
        <div className="relative max-w-3xl ml-auto mr-auto px-6 text-center">
            <Reveal>
                <div className="ml-auto mr-auto w-16 h-16 rounded-full bg-white/10 ring-1 ring-white/20 flex items-center justify-center mb-6">
                    <Heart size={28} className="text-rose-300 fill-rose-300 vh-beat" />
                </div>
                <h2 className="vh-serif text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-tight">Suma tu clínica a <em className="italic text-teal-200">Amigo Peludo</em></h2>
                <p className="mt-4 text-white/75 leading-relaxed max-w-xl ml-auto mr-auto">Solicita tu cuenta: revisamos los datos de tu clínica y te activamos el acceso al panel. Si ya usas Amigo Peludo, es la misma cuenta.</p>
                <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
                    <button onClick={onRegister} className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-[#2dd4bf] to-[#38bdf8] text-[#0f2f3a] font-bold text-sm shadow-[0_18px_40px_-14px_rgba(45,212,191,.6)] hover:-translate-y-0.5 transition-all">
                        Solicitar cuenta <ArrowRight size={17} />
                    </button>
                    <button onClick={onLogin} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white/10 text-white font-bold text-sm border border-white/25 hover:bg-white/20 transition-all">
                        Ingresar al panel
                    </button>
                </div>
            </Reveal>
        </div>
    </section>
);

export const VetHome: React.FC<{ onRegister: () => void; onLogin: () => void }> = ({ onRegister, onLogin }) => (
    <div className="font-sans text-[#0f2f3a] overflow-x-hidden">
        <HomeStyles />
        <Hero onRegister={onRegister} onLogin={onLogin} />
        <ValuesMarquee />
        <Journey />
        <SharedRecord />
        <Toolkit />
        <FinalCta onRegister={onRegister} onLogin={onLogin} />
    </div>
);

export default VetHome;
